import {
  Component,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SlotCalendarComponent, SlotCalendarItem } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { SlotItem } from '../../slots/data-access/slots.models';
import { CreateAppointmentRequest } from '../data-access/appointments.models';
import { buildContiguousSlotBlock } from '../utils/build-slot-block';

export interface AppointmentFormPrefill
{
  clinicId?: string;
  procedureTypeId?: string;
  deviceId?: string | null;
  firstSlotId?: string;
  firstSlotStartTime?: string;
  firstSlotEndTime?: string;
  preserveTimeOnly?: boolean;
  specialistUserId?: string | null;
}

export interface AppointmentSlotScopeChange
{
  clientId?: string;
  clinicId?: string;
  schedulingDate: string;
  procedureTypeId: string;
  deviceId?: string;
  specialistUserId?: string;
}

export type AppointmentFormMode = 'create' | 'edit';

type DeviceOption = {
  id: string;
  label: string;
  deviceTypeId?: string;
  clinicId?: string;
};

type SpecialistOption = {
  id: string;
  label: string;
  procedureTypeIds?: string[];
};

type ClientOption = {
  id: string;
  label: string;
  title?: string;
};

type ClientSpecialistIntent = 'any' | 'specific';

export type AppointmentClientSpecialistIntent = ClientSpecialistIntent;

const ANY_SPECIALIST_PREFERENCE = '__any__';

type AppointmentCalendarSlot = SlotItem & SlotCalendarItem;

type AnySpecialistSelectionChoice = {
  specialistUserId: string;
  specialistLabel: string;
  firstSlotId: string;
  slotIds: string[];
  clinicId: string;
  deviceId: string;
  deviceLabel: string | null;
  sessionStartTime: string;
  sessionEndTime: string;
};

type AnySpecialistTimeContext = {
  clinicId: string;
  startTime: string;
  endTime: string;
};

type ClinicOption = {
  id: string;
  label: string;
  timeZone?: string;
};

type AppointmentFormCascadeSelectionState = {
  clinicId: string;
  clientId: string;
  procedureTypeId: string;
  dateKey: string;
};

@Component({
  selector: 'app-appointment-form',
  standalone: true,
  imports: [ReactiveFormsModule, SlotCalendarComponent, TranslatePipe],
  template: `
    <form
      [formGroup]="form"
      class="appointment-form"
    >
      @if (clientIntentMode) {
        <div class="appointment-form__intent">
          @if (!lockedClientId) {
            <div
              class="appointment-form__field appointment-form__field--wide"
              [class.appointment-form__field--missing]="isRequiredSelectionMissing('clientId')"
            >
              <label for="appointment-clientId">{{ 'Client' | t }}</label>
              @if (clients.length > 0) {
                <select id="appointment-clientId" formControlName="clientId">
                  <option value="">{{ 'Select client' | t }}</option>
                  @for (item of clients; track item.id) {
                    <option [value]="item.id" [title]="item.title || item.label">{{ item.label }}</option>
                  }
                </select>
              } @else {
                <input
                  id="appointment-clientId"
                  [value]="'Client catalog is unavailable' | t"
                  disabled
                />
              }
            </div>
          }

          <div
            class="appointment-form__field appointment-form__field--wide"
            [class.appointment-form__field--missing]="isRequiredSelectionMissing('procedureTypeId')"
          >
            <label for="appointment-procedureTypeId">{{ 'Procedure Type' | t }}</label>
            <select id="appointment-procedureTypeId" formControlName="procedureTypeId">
              <option value="">{{ 'Select procedure' | t }}</option>
              @for (item of availableProcedureTypes; track item.id) {
                <option [value]="item.id">
                  {{ item.name }} ({{ item.defaultDurationMinutes }} min)
                </option>
              }
            </select>
            @if (procedureSelectionBlockedReason) {
              <div class="appointment-form__hint appointment-form__hint--warning">
                {{ procedureSelectionBlockedReason | t }}
              </div>
            }
          </div>

          <div class="appointment-form__field appointment-form__field--wide">
            <label for="appointment-specialistUserId">{{ 'Specialist' | t }}</label>
            <select
              id="appointment-specialistUserId"
              formControlName="clientSpecialistPreference"
              (change)="onClientSpecialistSelectChanged($event)"
            >
              <option [value]="anySpecialistPreferenceValue">{{ 'Any specialist' | t }}</option>
              @for (item of specialistOptions; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
            @if (specialistSelectionBlockedReason) {
              <div class="appointment-form__hint appointment-form__hint--warning">
                {{ specialistSelectionBlockedReason | t }}
              </div>
            }
          </div>
        </div>
      } @else {
        <div class="appointment-form__grid">
        <div class="appointment-form__field">
          <label for="appointment-clinicId">{{ 'Clinic' | t }}</label>
          @if (clinics.length > 0) {
            <select id="appointment-clinicId" formControlName="clinicId">
              <option value="">{{ 'Select clinic' | t }}</option>
              @for (item of clinics; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          } @else {
            <input
              id="appointment-clinicId"
              [value]="'Clinic catalog is unavailable' | t"
              disabled
            />
          }
        </div>

        <div class="appointment-form__field">
          <label for="appointment-clientId">{{ 'Client' | t }}</label>
          @if (lockedClientId) {
            <input
              id="appointment-clientId"
              [value]="lockedClientLabel"
              [title]="lockedClientTitle"
              disabled
            />
          } @else if (clients.length > 0) {
            <select id="appointment-clientId" formControlName="clientId">
              <option value="">{{ 'Select client' | t }}</option>
              @for (item of clients; track item.id) {
                <option [value]="item.id" [title]="item.title || item.label">{{ item.label }}</option>
              }
            </select>
            @if (canQuickCreateClient) {
              <div class="appointment-form__field-actions">
                <button type="button" class="appointment-form__link-action" (click)="requestQuickCreateClient()">
                  {{ 'Quick create client' | t }}
                </button>
              </div>
            }
          } @else {
            <input
              id="appointment-clientId"
              [value]="'Client catalog is unavailable' | t"
              disabled
            />
            @if (canQuickCreateClient) {
              <div class="appointment-form__field-actions">
                <button type="button" class="appointment-form__link-action" (click)="requestQuickCreateClient()">
                  {{ 'Create client now' | t }}
                </button>
              </div>
            }
          }
        </div>

        <div class="appointment-form__field">
          <label for="appointment-procedureTypeId">{{ 'Procedure Type' | t }}</label>
          <select id="appointment-procedureTypeId" formControlName="procedureTypeId">
            <option value="">{{ 'Select procedure' | t }}</option>
            @for (item of availableProcedureTypes; track item.id) {
              <option [value]="item.id">
                {{ item.name }} ({{ item.defaultDurationMinutes }} min)
              </option>
            }
          </select>
          @if (procedureSelectionBlockedReason) {
            <div class="appointment-form__hint appointment-form__hint--warning">
              {{ procedureSelectionBlockedReason | t }}
            </div>
          }
        </div>

        <div class="appointment-form__field">
          <label for="appointment-specialistUserId">{{ 'Specialist' | t }}</label>
          @if (usesAvailabilityCalendar) {
            <select
              id="appointment-specialistUserId"
              formControlName="clientSpecialistPreference"
              (change)="onClientSpecialistSelectChanged($event)"
            >
              <option [value]="anySpecialistPreferenceValue">{{ 'Any specialist' | t }}</option>
              @for (item of specialistOptions; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          } @else {
            <select
              id="appointment-specialistUserId"
              formControlName="specialistUserId"
              (change)="onSpecialistSelectChanged($event)"
            >
              <option value="">{{ 'Select specialist' | t }}</option>
              @for (item of specialistOptions; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          }
          @if (specialistSelectionBlockedReason) {
            <div class="appointment-form__hint appointment-form__hint--warning">
              {{ specialistSelectionBlockedReason | t }}
            </div>
          }
        </div>

        <div class="appointment-form__field">
          <label for="appointment-deviceId">{{ 'Device (optional override)' | t }}</label>
          @if (resolvedDeviceFieldOptions.length > 0) {
            <select id="appointment-deviceId" formControlName="deviceId">
              <option value="">{{ 'Auto-select by availability' | t }}</option>
              @for (item of resolvedDeviceFieldOptions; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          } @else {
            <input
              id="appointment-deviceId"
              [value]="(selectedProcedureRequiresDevice ? 'No compatible devices for selected procedure' : 'No device required for selected procedure') | t"
              disabled
            />
          }
        </div>
        </div>
      }

      @if (showConfirmationRequired) {
        <label class="appointment-form__checkbox">
          <input type="checkbox" formControlName="confirmationRequired" />
          {{ 'Confirmation required' | t }}
        </label>
      }

      <div class="appointment-form__calendar">
        <div class="appointment-form__calendar-head">
          <div class="appointment-form__calendar-title">
            {{ (usesAvailabilityCalendar ? 'Available time' : 'Slot Calendar') | t }}
          </div>
          <div class="appointment-form__calendar-meta">
            {{ (usesAvailabilityCalendar ? 'Choose a time. We will match the specialist and device.' : 'Pick specialist, then choose start time. Device will be auto-matched.') | t }}
          </div>
        </div>
        <lib-slot-calendar
          [slots]="filteredCalendarSlots"
          [singleRowPerHour]="true"
          [hideUnavailableHours]="mode === 'create' && !usesAvailabilityCalendar"
          [timeZone]="currentSchedulingTimeZone"
          [selectedDate]="calendarPickerDate"
          [minDate]="minCalendarDate"
          [selectedSlotId]="selectedSlotId"
          [highlightedSlotIds]="calendarHighlightedSlotIds"
          [showDatePicker]="true"
          [dateLabel]="'Date' | t"
          datePickerId="appointment-calendar-date"
          [emptySlotsText]="'No available slots for selected filters.' | t"
          [emptyDateText]="'No slots available for selected date.' | t"
          [selectableStatuses]="selectableCalendarStatuses"
          [allowPastSelection]="false"
          [showPastStatus]="true"
          [legendMode]="usesAvailabilityCalendar ? 'capacity' : 'auto'"
          (selectedDateChange)="onCalendarDateSelected($event)"
          (slotSelected)="onCalendarSlotPicked($event)"
        />
      </div>

      @if (pendingAnySpecialistSelection) {
        <div class="appointment-form__choice-panel">
          <div class="appointment-form__field">
            <label for="appointment-any-specialist-choice">{{ 'Choose specialist' | t }}</label>
            <select
              id="appointment-any-specialist-choice"
              formControlName="anySpecialistChoiceId"
              (change)="onAnySpecialistChoiceChanged($event)"
            >
              <option value="">{{ pendingAnySpecialistSelection.timeLabel }}</option>
              @for (choice of pendingAnySpecialistSelection.choices; track choice.specialistUserId) {
                <option [value]="choice.specialistUserId">
                  {{ choice.specialistLabel }}{{ choice.deviceLabel ? ' / ' + choice.deviceLabel : '' }}
                </option>
              }
            </select>
          </div>
        </div>
      }

      @if (!usesAvailabilityCalendar) {
        <div class="appointment-form__computed">
        <div><strong>{{ 'Computed slot count:' | t }}</strong> {{ computedSlotIds.length }}</div>
        <div><strong>{{ 'Computed slots:' | t }}</strong></div>
        <div class="appointment-form__slot-list">
          @if (computedSlotIds.length === 0) {
            <span>{{ 'Unable to build contiguous slot block.' | t }}</span>
          } @else {
            @for (slotId of computedSlotIds; track slotId) {
              <div>
                <div [title]="slotDisplayTitle(slotId)">{{ slotDisplayLabel(slotId) }}</div>
                @if (!hideTechnicalIds && showIdMeta(slotId)) {
                  <div class="appointment-form__id-meta">{{ slotId }}</div>
                }
              </div>
            }
          }
        </div>
        </div>
      }

      @if (errorText) {
        <div class="appointment-form__error">
          {{ errorText | t }}
        </div>
      }

      @if (validationErrorText) {
        <div class="appointment-form__error">
          {{ validationErrorText | t }}
        </div>
      }

      @if (catalogUnavailableReason) {
        <div class="appointment-form__error">
          {{ catalogUnavailableReason | t }}
        </div>
      }

        <button
          type="button"
          [disabled]="isSubmitDisabled"
          (click)="onSubmit()"
          class="appointment-form__submit"
        >
        {{ submitButtonText | t }}
      </button>
    </form>
  `,
  styles: [`
    .appointment-form {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 20px;
    }

    .appointment-form__grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      margin-bottom: 16px;
    }

    .appointment-form__intent {
      display: grid;
      gap: 16px;
      margin-bottom: 16px;
    }

    .appointment-form__field--wide {
      grid-column: 1 / -1;
    }

    .appointment-form__intent-panel {
      border: 1px solid #dbeafe;
      border-radius: 10px;
      background: #eff6ff;
      padding: 12px;
      display: grid;
      gap: 10px;
    }

    .appointment-form__intent-title {
      color: #1e3a8a;
      font-size: 13px;
      font-weight: 800;
    }

    .appointment-form__intent-options {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .appointment-form__intent-option {
      min-height: 40px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #ffffff;
      color: #1e40af;
      font-weight: 700;
      cursor: pointer;
    }

    .appointment-form__intent-option--active {
      border-color: #2563eb;
      background: #2563eb;
      color: #ffffff;
    }

    .appointment-form__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .appointment-form__field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .appointment-form__field input,
    .appointment-form__field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      box-sizing: border-box;
    }

    .appointment-form__field--missing label {
      color: #b91c1c;
    }

    .appointment-form__field--missing input,
    .appointment-form__field--missing select {
      border-color: #ef4444;
      background: #fff7f7;
      box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.16);
    }

    .appointment-form__field-actions {
      display: flex;
      justify-content: flex-end;
    }

    .appointment-form__link-action {
      background: transparent;
      border: none;
      color: #2563eb;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
    }

    .appointment-form__link-action:hover {
      text-decoration: underline;
    }

    .appointment-form__checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      color: #111827;
    }

    .appointment-form__calendar {
      margin-bottom: 16px;
      padding: 12px;
      border-radius: 10px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
    }

    .appointment-form__calendar-head {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 10px;
    }

    .appointment-form__calendar-title {
      font-size: 13px;
      font-weight: 700;
      color: #111827;
    }

    .appointment-form__calendar-meta {
      font-size: 12px;
      color: #6b7280;
    }

    .appointment-form__choice-panel {
      margin-bottom: 16px;
      padding: 12px;
      border-radius: 10px;
      border: 1px solid #bfdbfe;
      background: #eff6ff;
    }

    .appointment-form__computed {
      margin-bottom: 16px;
      padding: 12px;
      border-radius: 10px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      font-size: 13px;
    }

    .appointment-form__slot-list {
      margin-top: 8px;
      display: grid;
      gap: 8px;
      font-family: monospace;
      font-size: 12px;
      color: #374151;
    }

    .appointment-form__id-meta {
      margin-top: 2px;
      color: #6b7280;
      font-size: 11px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      word-break: break-all;
      line-height: 1.25;
    }

    .appointment-form__error {
      margin-bottom: 16px;
      color: #b91c1c;
      font-size: 14px;
    }

    .appointment-form__hint {
      font-size: 12px;
      margin-top: 2px;
    }

    .appointment-form__hint--warning {
      color: #9a3412;
    }

    .appointment-form__submit {
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 16px;
      cursor: pointer;
    }

    .appointment-form__submit:disabled {
      opacity: 0.7;
      cursor: default;
    }

    @media (max-width: 900px) {
      .appointment-form {
        padding: 16px;
      }

      .appointment-form__grid {
        grid-template-columns: 1fr;
        gap: 14px;
      }

      .appointment-form__intent-options {
        grid-template-columns: 1fr;
      }

      .appointment-form__field-actions {
        justify-content: flex-start;
      }

      .appointment-form__submit {
        width: 100%;
      }
    }

    @media (max-width: 640px) {
      .appointment-form {
        padding: 14px;
        border-radius: 10px;
      }

      .appointment-form__field input,
      .appointment-form__field select {
        height: 42px;
        padding: 0 10px;
      }

      .appointment-form__calendar,
      .appointment-form__computed {
        padding: 10px;
      }

      .appointment-form__calendar-head {
        align-items: flex-start;
        flex-direction: column;
      }

      .appointment-form__slot-list {
        font-size: 11px;
      }

      .appointment-form__id-meta {
        display: none;
      }
    }
  `]
})
export class AppointmentFormComponent implements OnChanges
{
  private readonly locale = inject(LOCALE_ID);
  private readonly formBuilder = inject(FormBuilder);
  private readonly slotDurationMinutes = 15;

  @Input()
  public isSubmitting = false;

  @Input()
  public mode: AppointmentFormMode = 'create';

  @Input()
  public errorText: string | null = null;

  @Input()
  public prefill: AppointmentFormPrefill | null = null;

  @Input()
  public procedureTypes: ProcedureTypeItem[] = [];

  @Input()
  public clinicProcedureAssignments: Record<string, string[]> = {};

  @Input()
  public availableSlots: SlotItem[] = [];

  @Input()
  public lockedClientId: string | null = null;

  @Input()
  public defaultClientId: string | null = null;

  @Input()
  public canQuickCreateClient = false;

  @Input()
  public clinics: ClinicOption[] = [];

  @Input()
  public devices: DeviceOption[] = [];

  @Input()
  public specialists: SpecialistOption[] = [];

  @Input()
  public clients: ClientOption[] = [];

  @Input()
  public hideTechnicalIds = false;

  @Input()
  public showConfirmationRequired = true;

  @Input()
  public submitLabel: string | null = null;

  @Input()
  public submittingLabel: string | null = null;

  @Input()
  public clientIntentMode = false;

  @Input()
  public availabilityCalendarMode = false;

  @Input()
  public selectPrefilledSlotOnOpen = false;

  @Input()
  public minCalendarDate: string | null = null;

  @Input()
  public initialClientSpecialistIntent: AppointmentClientSpecialistIntent | null = null;

  @Input()
  public suggestedSpecialistUserId: string | null = null;

  @Output()
  public readonly submitted = new EventEmitter<CreateAppointmentRequest>();

  @Output()
  public readonly quickCreateClientRequested = new EventEmitter<void>();

  @Output()
  public readonly slotScopeChanged = new EventEmitter<AppointmentSlotScopeChange>();

  protected computedSlotIds: string[] = [];
  protected validationErrorText: string | null = null;
  protected readonly anySpecialistPreferenceValue = ANY_SPECIALIST_PREFERENCE;
  protected pendingAnySpecialistSelection: { timeLabel: string; choices: AnySpecialistSelectionChoice[] } | null = null;
  protected pendingAnySpecialistChoiceId = '';
  private resolvedAnySpecialistSelection: AnySpecialistSelectionChoice | null = null;
  private pendingAnySpecialistTimeContext: AnySpecialistTimeContext | null = null;
  private selectedDateKey = this.todayDateKey();
  private preferredFirstSlotId: string | null = null;
  private hasUserPickedSlot = false;
  private hasUserChangedClientSpecialistIntent = false;
  private lastEmittedSlotScopeKey = '';
  private filteredCalendarSlotsCache: AppointmentCalendarSlot[] = [];
  private filteredCalendarSlotsCacheKey = '';
  private availableDeviceIdsByTimeKey = new Map<string, Set<string>>();
  private selectableDeviceSlotKeys = new Set<string>();
  private specialistProcedureIdsById = new Map<string, Set<string>>();
  private calendarHighlightedSlotIdsCache: string[] = [];
  private calendarHighlightedSlotIdsCacheKey = '';
  private appliedSuggestedSpecialistUserId: string | null = null;
  private appliedSuggestedSpecialistScopeKey = '';
  private lastSuggestionControlScopeKey = '';
  private lastCascadeSelectionState: AppointmentFormCascadeSelectionState | null = null;
  private readonly createSelectableCalendarStatuses = ['available'];
  private readonly editSelectableCalendarStatuses = ['available', 'reserved'];

  protected readonly form = this.formBuilder.group({
    clinicId: ['', [Validators.required]],
    clientId: ['', [Validators.required]],
    procedureTypeId: ['', [Validators.required]],
    firstSlotId: ['', [Validators.required]],
    deviceId: [''],
    specialistUserId: ['', [Validators.required]],
    clientSpecialistPreference: [ANY_SPECIALIST_PREFERENCE],
    anySpecialistChoiceId: [''],
    confirmationRequired: [true]
  });

  protected get specialistOptions(): Array<{ id: string; label: string }>
  {
    const selectedProcedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    const byId = new Map<string, { label: string; procedureTypeIds: string[] }>();

    for (const option of this.specialists) {
      byId.set(option.id, {
        label: option.label,
        procedureTypeIds: option.procedureTypeIds ?? [],
      });
    }

    for (const slot of this.availableSlots) {
      if (!slot.specialistUserId || byId.has(slot.specialistUserId)) {
        continue;
      }

      byId.set(slot.specialistUserId, {
        label: this.readableId(slot.specialistUserId),
        procedureTypeIds: [],
      });
    }

    const allSpecialists = Array.from(byId.entries())
      .map(([id, option]) => ({ id, label: option.label, procedureTypeIds: option.procedureTypeIds }));

    if (!selectedProcedureTypeId) {
      return allSpecialists.map(({ id, label }) => ({ id, label }));
    }

    const explicitlyAssigned = allSpecialists.filter((option) =>
      option.procedureTypeIds.includes(selectedProcedureTypeId),
    );

    if (explicitlyAssigned.length > 0) {
      return explicitlyAssigned.map(({ id, label }) => ({ id, label }));
    }

    // If procedure has no explicit specialist assignments yet, allow any specialist.
    return allSpecialists.map(({ id, label }) => ({ id, label }));
  }

  protected get submitButtonText(): string
  {
    if (this.isSubmitting) {
      return this.submittingLabel?.trim()
        || (this.mode === 'edit' ? 'Updating...' : 'Creating...');
    }

    return this.submitLabel?.trim()
      || (this.mode === 'edit' ? 'Update appointment' : 'Create appointment');
  }

  protected get isSubmitDisabled(): boolean
  {
    return this.isSubmitting ||
      !!this.procedureSelectionBlockedReason ||
      !!this.specialistSelectionBlockedReason ||
      !!this.catalogUnavailableReason ||
      this.computedSlotIds.length === 0;
  }

  protected get specialistSelectionBlockedReason(): string | null
  {
    const selectedProcedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    if (!selectedProcedureTypeId) {
      return null;
    }

    return this.specialistOptions.length > 0
      ? null
      : 'No specialists are available yet.';
  }

  protected get usesAvailabilityCalendar(): boolean
  {
    return this.clientIntentMode || this.availabilityCalendarMode;
  }

  protected isRequiredSelectionMissing(controlName: 'clinicId' | 'clientId' | 'procedureTypeId' | 'firstSlotId' | 'specialistUserId'): boolean
  {
    const control = this.form.controls[controlName];
    return !control.disabled && control.invalid;
  }

  protected get catalogUnavailableReason(): string | null
  {
    if (this.clinics.length === 0 && !this.lockedClientId && this.clients.length === 0) {
      return 'Clinic and client catalogs are unavailable. Reload page context and try again.';
    }

    if (this.clinics.length === 0) {
      return 'Clinic catalog is unavailable. Reload page context and try again.';
    }

    if (!this.lockedClientId && this.clients.length === 0) {
      return 'Client catalog is unavailable. Reload page context and try again.';
    }

    return null;
  }

  protected get procedureSelectionBlockedReason(): string | null
  {
    const value = this.form.getRawValue();
    const clinicId = value.clinicId?.trim() ?? '';
    if (!clinicId) {
      return null;
    }

    if (this.availableProcedureTypes.length > 0) {
      return null;
    }

    const assigned = this.clinicProcedureAssignments[clinicId] ?? [];
    if (assigned.length === 0) {
      return 'No procedures are assigned to this clinic yet.';
    }

    const selectedSpecialistId = this.usesAvailabilityCalendar
      ? this.selectedClientPreferenceSpecialistUserId()
      : (value.specialistUserId?.trim() ?? '');
    if (selectedSpecialistId) {
      return 'Selected specialist has no procedures available in this clinic.';
    }

    return 'No compatible procedures are available for selected clinic configuration.';
  }

  protected get compatibleDeviceOptions(): DeviceOption[]
  {
    const value = this.form.getRawValue();
    const clinicId = value.clinicId?.trim() ?? '';
    const procedureTypeId = value.procedureTypeId?.trim() ?? '';
    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);

    if (!selectedProcedure) {
      return [];
    }

    return this.deviceOptions.filter((device) =>
    {
      const sameDeviceType = (device.deviceTypeId ?? '') === selectedProcedure.deviceTypeId;
      if (!sameDeviceType) {
        return false;
      }

      const deviceClinic = device.clinicId?.trim() ?? '';
      if (!clinicId || !deviceClinic) {
        return true;
      }

      return clinicId === deviceClinic;
    });
  }

  protected get selectedProcedureRequiresDevice(): boolean
  {
    const procedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);
    return !!selectedProcedure?.deviceTypeId;
  }

  protected get resolvedDeviceFieldOptions(): DeviceOption[]
  {
    const compatibleDevices = this.compatibleDeviceOptions;
    const selectedDeviceId = this.form.getRawValue().deviceId?.trim() ?? '';
    if (!selectedDeviceId) {
      return compatibleDevices;
    }

    if (compatibleDevices.some((item) => item.id === selectedDeviceId)) {
      return compatibleDevices;
    }

    const selectedDevice =
      this.deviceOptions.find((item) => item.id === selectedDeviceId)
      ?? this.devices.find((item) => item.id === selectedDeviceId);

    if (!selectedDevice) {
      return compatibleDevices;
    }

    return [selectedDevice, ...compatibleDevices];
  }

  protected get lockedClientLabel(): string
  {
    const lockedClientId = this.lockedClientId?.trim() ?? '';
    if (!lockedClientId) {
      return '';
    }

    return this.clients.find((item) => item.id === lockedClientId)?.label ?? this.readableId(lockedClientId);
  }

  protected get lockedClientTitle(): string
  {
    const lockedClientId = this.lockedClientId?.trim() ?? '';
    if (!lockedClientId) {
      return '';
    }

    const matched = this.clients.find((item) => item.id === lockedClientId);
    return matched?.title?.trim() || matched?.label || this.lockedClientLabel;
  }

  private get deviceOptions(): DeviceOption[]
  {
    const byId = new Map<string, DeviceOption>();

    for (const option of this.devices) {
      byId.set(option.id, option);
    }

    for (const slot of this.availableSlots) {
      if (!slot.deviceId || byId.has(slot.deviceId)) {
        continue;
      }

      byId.set(slot.deviceId, {
        id: slot.deviceId,
        label: this.readableId(slot.deviceId)
      });
    }

    return Array.from(byId.values());
  }

  protected get filteredCalendarSlots(): AppointmentCalendarSlot[]
  {
    const value = this.form.getRawValue();
    const clinicId = value.clinicId?.trim() ?? '';
    const procedureTypeId = value.procedureTypeId?.trim() ?? '';
    const allowAnySpecialist = this.isAnySpecialistWorkflowActive();
    const specialistUserId = allowAnySpecialist
      ? ''
      : this.selectedClientPreferenceSpecialistUserId();
    const selectedDeviceId = this.clientIntentMode ? '' : (value.deviceId?.trim() ?? '');
    const cacheKey = [
      clinicId,
      procedureTypeId,
      specialistUserId,
      selectedDeviceId,
      allowAnySpecialist ? 'any' : 'specific',
      this.clientIntentMode ? 'client-intent' : 'standard',
      this.usesAvailabilityCalendar ? 'availability-calendar' : 'slot-calendar',
    ].join('|');

    if (this.filteredCalendarSlotsCacheKey === cacheKey) {
      return this.filteredCalendarSlotsCache;
    }

    if (!procedureTypeId || (!specialistUserId && !allowAnySpecialist)) {
      this.filteredCalendarSlotsCacheKey = cacheKey;
      this.filteredCalendarSlotsCache = [];
      return this.filteredCalendarSlotsCache;
    }

    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);
    const procedureRequiresDevice = !!selectedProcedure?.deviceTypeId;

    const compatibleDeviceIds = new Set(
      this.compatibleDeviceOptions.map((item) => item.id),
    );

    if (procedureRequiresDevice && compatibleDeviceIds.size === 0 && this.usesAvailabilityCalendar) {
      this.filteredCalendarSlotsCacheKey = cacheKey;
      this.filteredCalendarSlotsCache = [];
      return this.filteredCalendarSlotsCache;
    }

    const filteredSlots = this.availableSlots
      .filter((slot) =>
      {
        if (clinicId && slot.clinicId !== clinicId) {
          return false;
        }

        if (!allowAnySpecialist && slot.specialistUserId !== specialistUserId) {
          return false;
        }

        if (allowAnySpecialist && !slot.specialistUserId) {
          return false;
        }

        if (slot.deviceId) {
          return false;
        }

        if (!this.usesAvailabilityCalendar) {
          return true;
        }

        if (this.usesAvailabilityCalendar && !this.isCalendarSlotSelectable(slot)) {
          return true;
        }

        if (!procedureRequiresDevice) {
          return true;
        }

        if (selectedDeviceId) {
          return this.hasMatchingDeviceSlot(
            selectedDeviceId,
            slot.startTime,
            slot.endTime,
            clinicId,
          );
        }

        return this.hasAnyCompatibleDeviceSlot(
          compatibleDeviceIds,
          slot.startTime,
          slot.endTime,
          clinicId,
        );
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    if (this.usesAvailabilityCalendar && allowAnySpecialist) {
      this.filteredCalendarSlotsCacheKey = cacheKey;
      this.filteredCalendarSlotsCache = this.buildAnySpecialistCalendarSlots(
        filteredSlots,
        procedureTypeId,
      );
      return this.filteredCalendarSlotsCache;
    }

    if (this.usesAvailabilityCalendar) {
      this.filteredCalendarSlotsCacheKey = cacheKey;
      this.filteredCalendarSlotsCache = filteredSlots.map((slot) =>
        this.buildSpecificSpecialistCalendarSlot(slot, procedureTypeId),
      );
      return this.filteredCalendarSlotsCache;
    }

    this.filteredCalendarSlotsCacheKey = cacheKey;
    this.filteredCalendarSlotsCache = filteredSlots.map((slot): AppointmentCalendarSlot => {
      if (!procedureRequiresDevice || !this.isCalendarSlotCapacityAvailable(slot)) {
        return slot;
      }
      const deviceAvailable = selectedDeviceId
        ? this.hasMatchingDeviceSlot(selectedDeviceId, slot.startTime, slot.endTime, clinicId)
        : this.hasAnyCompatibleDeviceSlot(compatibleDeviceIds, slot.startTime, slot.endTime, clinicId);
      return deviceAvailable ? slot : {
        ...slot,
        status: 'blocked',
        hardUnavailable: true,
        secondaryLabel: 'Device unavailable',
      };
    });
    return this.filteredCalendarSlotsCache;
  }

  private invalidateFilteredCalendarSlotsCache(): void {
    this.filteredCalendarSlotsCacheKey = '';
    this.filteredCalendarSlotsCache = [];
  }

  private invalidateCalendarHighlightedSlotIdsCache(): void {
    this.calendarHighlightedSlotIdsCacheKey = '';
    this.calendarHighlightedSlotIdsCache = [];
  }

  private rebuildAvailabilityIndexes(): void {
    this.availableDeviceIdsByTimeKey = new Map<string, Set<string>>();
    this.selectableDeviceSlotKeys = new Set<string>();
    this.specialistProcedureIdsById = new Map(
      this.specialists.map((item) => [
        item.id,
        new Set(item.procedureTypeIds ?? []),
      ]),
    );

    for (const slot of this.availableSlots) {
      const deviceId = slot.deviceId?.trim() ?? '';
      if (!deviceId || !this.isCalendarSlotCapacityAvailable(slot)) {
        continue;
      }

      this.selectableDeviceSlotKeys.add(this.deviceSlotKey(
        deviceId,
        slot.startTime,
        slot.endTime,
        slot.clinicId,
      ));
      this.selectableDeviceSlotKeys.add(this.deviceSlotKey(
        deviceId,
        slot.startTime,
        slot.endTime,
        '',
      ));

      if (slot.status !== 'available') {
        continue;
      }

      const timeKey = this.deviceTimeKey(slot.startTime, slot.endTime, slot.clinicId);
      const deviceIds = this.availableDeviceIdsByTimeKey.get(timeKey) ?? new Set<string>();
      deviceIds.add(deviceId);
      this.availableDeviceIdsByTimeKey.set(timeKey, deviceIds);

      const wildcardTimeKey = this.deviceTimeKey(slot.startTime, slot.endTime, '');
      const wildcardDeviceIds = this.availableDeviceIdsByTimeKey.get(wildcardTimeKey) ?? new Set<string>();
      wildcardDeviceIds.add(deviceId);
      this.availableDeviceIdsByTimeKey.set(wildcardTimeKey, wildcardDeviceIds);
    }
  }

  private get selectableCalendarSlots(): AppointmentCalendarSlot[]
  {
    return this.filteredCalendarSlots.filter((slot) => this.isCalendarSlotSelectable(slot));
  }

  private buildAnySpecialistCalendarSlots(
    slots: SlotItem[],
    procedureTypeId: string,
  ): AppointmentCalendarSlot[] {
    const groups = new Map<string, SlotItem[]>();

    for (const slot of slots) {
      if (!slot.specialistUserId || !this.specialistCanPerformProcedure(slot.specialistUserId, procedureTypeId)) {
        continue;
      }

      const key = [slot.clinicId, slot.startTime, slot.endTime].join('|');
      const list = groups.get(key) ?? [];
      list.push(slot);
      groups.set(key, list);
    }

    return Array.from(groups.values())
      .map((group) => this.buildAnySpecialistCalendarSlot(
        group,
        procedureTypeId,
      ))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  private buildAnySpecialistCalendarSlot(
    group: SlotItem[],
    procedureTypeId: string,
  ): AppointmentCalendarSlot {
    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);
    const procedureDeviceTypeId = selectedProcedure?.deviceTypeId ?? null;
    const requiresDevice = !!procedureDeviceTypeId;
    const clinicId = group[0]?.clinicId ?? '';
    const compatibleDeviceIds = requiresDevice
      ? new Set(
        this.devices
          .filter((device) =>
            (device.deviceTypeId ?? '') === procedureDeviceTypeId &&
            (!clinicId || !device.clinicId || device.clinicId === clinicId),
          )
          .map((device) => device.id),
      )
      : new Set<string>();
    const availableSlots = group.filter((slot) =>
      !!slot.specialistUserId &&
      this.specialistCanPerformProcedure(slot.specialistUserId, procedureTypeId) &&
      slot.status === 'available',
    );
    const bookableSlots = group.filter((slot) =>
      !!slot.specialistUserId &&
      this.specialistCanPerformProcedure(slot.specialistUserId, procedureTypeId) &&
      this.isCalendarSlotSelectable(slot),
    );
    const representative = bookableSlots[0] ?? availableSlots[0] ?? group[0];
    const totalCount = new Set(group.map((slot) => slot.specialistUserId).filter(Boolean)).size;
    const availableSpecialistCount = new Set(availableSlots.map((slot) => slot.specialistUserId).filter(Boolean)).size;
    const bookableCount = new Set(bookableSlots.map((slot) => slot.specialistUserId).filter(Boolean)).size;
    const occupiedCount = new Set(
      group
        .filter((slot) => slot.status !== 'available')
        .map((slot) => slot.specialistUserId)
        .filter(Boolean),
    ).size;
    const availableDeviceCount = requiresDevice
      ? this.countAvailableCompatibleDevices(
        compatibleDeviceIds,
        representative.startTime,
        representative.endTime,
        clinicId,
      )
      : 0;
    const totalResourceCount = totalCount + (requiresDevice ? compatibleDeviceIds.size : 0);
    const availableResourceCount = availableSpecialistCount + (requiresDevice ? availableDeviceCount : 0);
    const unavailableResourceCount = Math.max(0, totalResourceCount - availableResourceCount);
    const hardUnavailable = availableSpecialistCount === 0 || (requiresDevice && availableDeviceCount === 0);
    const allSlotsArePast = group.every((slot) => this.isSlotInPast(slot));

    return {
      ...representative,
      id: this.anySpecialistCalendarSlotId(
        representative.clinicId,
        representative.startTime,
        representative.endTime,
      ),
      status: bookableCount > 0 ? 'available' : representative.status,
      primaryLabel: `${availableSpecialistCount}/${totalCount} specialists`,
      secondaryLabel: occupiedCount > 0
        ? `${occupiedCount} occupied`
        : allSlotsArePast
          ? 'Past'
          : 'All matching specialists free',
      metaLabel: null,
      availableCount: availableResourceCount,
      occupiedCount: unavailableResourceCount,
      totalCount: totalResourceCount,
      hardUnavailable,
    };
  }

  private buildSpecificSpecialistCalendarSlot(
    slot: SlotItem,
    procedureTypeId: string,
  ): AppointmentCalendarSlot {
    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);
    const procedureDeviceTypeId = selectedProcedure?.deviceTypeId ?? null;
    const requiresDevice = !!procedureDeviceTypeId;
    const clinicId = slot.clinicId ?? '';
    const compatibleDeviceIds = requiresDevice
      ? new Set(
        this.devices
          .filter((device) =>
            (device.deviceTypeId ?? '') === procedureDeviceTypeId &&
            (!clinicId || !device.clinicId || device.clinicId === clinicId),
          )
          .map((device) => device.id),
      )
      : new Set<string>();
    const availableSpecialistCount = slot.status === 'available' ? 1 : 0;
    const occupiedSpecialistCount = slot.status === 'available' ? 0 : 1;
    const availableDeviceCount = requiresDevice
      ? this.countAvailableCompatibleDevices(
        compatibleDeviceIds,
        slot.startTime,
        slot.endTime,
        clinicId,
      )
      : 0;
    const totalResourceCount = 1 + (requiresDevice ? compatibleDeviceIds.size : 0);
    const availableResourceCount = availableSpecialistCount + (requiresDevice ? availableDeviceCount : 0);
    const unavailableResourceCount = Math.max(0, totalResourceCount - availableResourceCount);
    const hardUnavailable = availableSpecialistCount === 0 || (requiresDevice && availableDeviceCount === 0);

    return {
      ...slot,
      primaryLabel: `${availableSpecialistCount}/1 specialists`,
      secondaryLabel: occupiedSpecialistCount > 0
        ? 'Specialist occupied'
        : this.isSlotInPast(slot)
          ? 'Past'
          : 'Specialist free',
      metaLabel: requiresDevice
        ? `${availableDeviceCount}/${compatibleDeviceIds.size} devices`
        : null,
      availableCount: availableResourceCount,
      occupiedCount: unavailableResourceCount,
      totalCount: totalResourceCount,
      hardUnavailable,
    };
  }

  private resolveAnySpecialistSelectionChoices(context: AnySpecialistTimeContext): AnySpecialistSelectionChoice[] {
    const procedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    const selectedProcedure = this.availableProcedureTypes.find((item) => item.id === procedureTypeId);
    if (!procedureTypeId || !selectedProcedure) {
      return [];
    }

    const requiresDevice = !!selectedProcedure.deviceTypeId;
    const compatibleDeviceIds = new Set(
      this.devices
        .filter((device) =>
          (device.deviceTypeId ?? '') === selectedProcedure.deviceTypeId &&
          (!context.clinicId || !device.clinicId || device.clinicId === context.clinicId),
        )
        .map((device) => device.id),
    );

    const firstSlots = this.availableSlots
      .filter((slot) =>
        !slot.deviceId &&
        slot.clinicId === context.clinicId &&
        slot.startTime === context.startTime &&
        !!slot.specialistUserId &&
        this.specialistCanPerformProcedure(slot.specialistUserId, procedureTypeId) &&
        this.isCalendarSlotSelectable(slot),
      )
      .sort((a, b) => this.specialistLabel(a.specialistUserId).localeCompare(this.specialistLabel(b.specialistUserId)));

    const choices: AnySpecialistSelectionChoice[] = [];

    for (const firstSlot of firstSlots) {
      const specialistUserId = firstSlot.specialistUserId;
      if (!specialistUserId) {
        continue;
      }

      const candidateSlots = this.selectableSpecialistSlots(specialistUserId, firstSlot.clinicId);
      const blockSlots = this.buildContiguousSlotBlockFromTime(
        firstSlot.startTime,
        candidateSlots,
        selectedProcedure.defaultDurationMinutes,
      );
      const slotIds = blockSlots.map((slot) => slot.id);

      const deviceId = requiresDevice
        ? this.resolveDeviceForTimeBlock(blockSlots, firstSlot.clinicId, compatibleDeviceIds)
        : '';
      if (slotIds.length === 0 || (requiresDevice && !deviceId)) {
        continue;
      }
      const lastSlot = blockSlots[blockSlots.length - 1];

      choices.push({
        specialistUserId,
        specialistLabel: this.specialistLabel(specialistUserId),
        firstSlotId: firstSlot.id,
        slotIds,
        clinicId: firstSlot.clinicId,
        deviceId: deviceId ?? '',
        deviceLabel: deviceId ? this.deviceLabel(deviceId) : null,
        sessionStartTime: firstSlot.startTime,
        sessionEndTime: lastSlot?.endTime ?? firstSlot.endTime,
      });
    }

    return choices;
  }

  protected get calendarPickerDate(): string
  {
    return this.selectedDateKey;
  }

  protected get selectedSlotId(): string | null
  {
    if (
      this.isAnySpecialistWorkflowActive() &&
      !this.resolvedAnySpecialistSelection &&
      this.pendingAnySpecialistTimeContext
    ) {
      const context = this.pendingAnySpecialistTimeContext;
      return this.anySpecialistCalendarSlotId(context.clinicId, context.startTime, context.endTime);
    }

    const firstSlotId =
      this.resolvedAnySpecialistSelection?.firstSlotId
      ?? this.form.getRawValue().firstSlotId?.trim()
      ?? '';
    if (!firstSlotId) {
      return null;
    }

    return this.findCalendarSlotIdForActualSlotId(firstSlotId) ?? firstSlotId;
  }

  protected get calendarHighlightedSlotIds(): string[]
  {
    if (!this.isAnySpecialistWorkflowActive()) {
      return this.computedSlotIds;
    }

    const slotIds = this.resolvedAnySpecialistSelection?.slotIds ?? this.computedSlotIds;
    if (slotIds.length === 0 && this.pendingAnySpecialistSelection) {
      const cacheKey = `choices|${this.pendingAnySpecialistSelection.choices
        .map((choice) => choice.slotIds.join(','))
        .join('|')}`;
      if (this.calendarHighlightedSlotIdsCacheKey !== cacheKey) {
        this.calendarHighlightedSlotIdsCacheKey = cacheKey;
        this.calendarHighlightedSlotIdsCache = this.anySpecialistChoiceHighlightedSlotIds(
          this.pendingAnySpecialistSelection.choices,
        );
      }
      return this.calendarHighlightedSlotIdsCache;
    }

    const cacheKey = `slots|${slotIds.join('|')}`;
    if (this.calendarHighlightedSlotIdsCacheKey !== cacheKey) {
      this.calendarHighlightedSlotIdsCacheKey = cacheKey;
      this.calendarHighlightedSlotIdsCache = Array.from(new Set(
        slotIds.map((slotId) => this.findCalendarSlotIdForActualSlotId(slotId) ?? slotId),
      ));
    }
    return this.calendarHighlightedSlotIdsCache;
  }

  protected get selectableCalendarStatuses(): string[]
  {
    return this.mode === 'edit'
      ? this.editSelectableCalendarStatuses
      : this.createSelectableCalendarStatuses;
  }

  private anySpecialistCalendarSlotId(clinicId: string, startTime: string, endTime: string): string {
    return `any|${clinicId}|${startTime}|${endTime}`;
  }

  private parseAnySpecialistCalendarSlotId(slotId: string): AnySpecialistTimeContext | null {
    const parts = slotId.split('|');
    if (parts.length !== 4 || parts[0] !== 'any') {
      return null;
    }

    const [, clinicId, startTime, endTime] = parts;
    if (!clinicId || !startTime || !endTime) {
      return null;
    }

    return { clinicId, startTime, endTime };
  }

  private resolveAnySpecialistTimeContext(slotId: string): AnySpecialistTimeContext | null {
    const parsed = this.parseAnySpecialistCalendarSlotId(slotId);
    if (parsed) {
      return parsed;
    }

    const slot =
      this.filteredCalendarSlots.find((item) => item.id === slotId)
      ?? this.availableSlots.find((item) => item.id === slotId);
    if (!slot) {
      return null;
    }

    return {
      clinicId: slot.clinicId,
      startTime: slot.startTime,
      endTime: slot.endTime,
    };
  }

  public constructor()
  {
    this.form.valueChanges.subscribe(() =>
    {
      this.invalidateFilteredCalendarSlotsCache();
      this.validationErrorText = null;
      this.applyCascadingSelectionResets();
      this.clearAppliedSuggestedSpecialistWhenScopeChanged();
      this.ensureProcedureSelectionAllowed();
      this.syncDeviceSelectionFromProcedureType();
      this.syncSpecialistSelectionFromProcedureType();
      this.applySuggestedSpecialist();
      this.syncDerivedFieldsFromFirstSlot();
      this.ensureSelectedSlotStillVisible();
      this.rebuildComputedSlotIds();
      this.emitSlotScopeChanged();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['minCalendarDate']) {
      this.ensureSelectedDateWithinMin();
    }

    if (changes['prefill'] && this.prefill)
    {
      const prefillDateKey = this.dateKey(this.prefill.firstSlotStartTime ?? '');
      if (prefillDateKey) {
        this.selectedDateKey = prefillDateKey;
      }

      const prefillFirstSlotId = this.prefill.firstSlotId?.trim() ?? '';
      const prefillSpecialistUserId = this.prefill.specialistUserId?.trim() ?? '';
      const nextSpecialistUserId =
        this.usesAvailabilityCalendar && this.mode !== 'edit'
          ? ''
          : prefillSpecialistUserId || (this.prefill.specialistUserId ?? '');
      this.preferredFirstSlotId = prefillFirstSlotId || null;
      this.appliedSuggestedSpecialistUserId = null;
      this.appliedSuggestedSpecialistScopeKey = '';
      this.lastSuggestionControlScopeKey = this.currentSpecialistSuggestionScopeKey();
      this.hasUserPickedSlot = false;
      this.hasUserChangedClientSpecialistIntent = false;
      this.resetAnySpecialistWorkflowState();

      if (this.usesAvailabilityCalendar) {
        const nextPreference =
          this.mode === 'edit' && prefillSpecialistUserId
            ? prefillSpecialistUserId
            : ANY_SPECIALIST_PREFERENCE;
        this.form.patchValue(
          {
            clientSpecialistPreference: nextPreference,
            specialistUserId: nextPreference === ANY_SPECIALIST_PREFERENCE ? '' : nextPreference,
          },
          { emitEvent: false },
        );
      }

      this.form.patchValue({
        clinicId: this.prefill.clinicId ?? '',
        procedureTypeId: this.prefill.procedureTypeId ?? '',
        deviceId: this.prefill.deviceId ?? '',
        firstSlotId: this.shouldSelectPrefilledSlotOnOpen(prefillFirstSlotId)
          ? prefillFirstSlotId
          : '',
        specialistUserId: nextSpecialistUserId
      });
      this.syncInitialClientSpecialistIntentFromPrefill();
      this.applyInitialClientSpecialistIntent();
      this.ensureSelectedDateWithinMin();
    }

    if (changes['initialClientSpecialistIntent']) {
      this.applyInitialClientSpecialistIntent();
    }

    if (
      changes['suggestedSpecialistUserId'] ||
      changes['specialists'] ||
      changes['procedureTypes']
    ) {
      this.applySuggestedSpecialist();
    }

    if (changes['clientIntentMode'] || changes['mode']) {
      this.syncInitialClientSpecialistIntentFromPrefill();
    }

    if (changes['lockedClientId'])
    {
      if (this.lockedClientId)
      {
        this.form.patchValue({ clientId: this.lockedClientId });
        this.form.controls.clientId.disable({ emitEvent: false });
      }
      else
      {
        this.form.controls.clientId.enable({ emitEvent: false });
      }
    }

    if (changes['defaultClientId'] && this.defaultClientId && !this.lockedClientId)
    {
      this.form.patchValue({ clientId: this.defaultClientId });
    }

    if (
      changes['specialists'] ||
      changes['availableSlots']
    )
    {
      this.rebuildAvailabilityIndexes();
    }

    if (
      changes['procedureTypes'] ||
      changes['availableSlots'] ||
      changes['prefill'] ||
      changes['devices'] ||
      changes['specialists'] ||
      changes['clientIntentMode'] ||
      changes['clinicProcedureAssignments']
    )
    {
      this.invalidateFilteredCalendarSlotsCache();
      this.invalidateCalendarHighlightedSlotIdsCache();
      this.syncPrefillFieldsFromPreferredSlot();
      this.syncDeviceSelectionFromProcedureType();
      this.syncSpecialistSelectionFromProcedureType();
      this.ensureProcedureSelectionAllowed();
      this.ensureSelectedSlotStillVisible();
      this.rebuildComputedSlotIds();
    }
  }

  public onSubmit(): void
  {
    if (this.catalogUnavailableReason) {
      this.validationErrorText = this.catalogUnavailableReason;
      return;
    }

    this.validationErrorText = null;
    this.restorePendingAnySpecialistChoiceFromForm();
    this.restoreResolvedAnySpecialistSelection();
    this.syncDerivedFieldsFromFirstSlot();
    this.rebuildComputedSlotIds();

    if (this.procedureSelectionBlockedReason) {
      this.validationErrorText = this.procedureSelectionBlockedReason;
      return;
    }

    if (this.specialistSelectionBlockedReason) {
      this.validationErrorText = this.specialistSelectionBlockedReason;
      return;
    }

    if (this.form.invalid)
    {
      this.form.markAllAsTouched();
      this.validationErrorText = this.buildValidationErrorText();
      return;
    }

    if (this.computedSlotIds.length === 0)
    {
      this.validationErrorText = 'Unable to build contiguous slot block.';
      return;
    }

    const value = this.form.getRawValue();

    this.submitted.emit({
      clinicId: value.clinicId ?? '',
      clientId: value.clientId ?? '',
      procedureTypeId: value.procedureTypeId ?? '',
      slotIds: this.resolvedAnySpecialistSelection?.slotIds ?? this.computedSlotIds,
      deviceId: value.deviceId?.trim() ? value.deviceId.trim() : undefined,
      specialistUserId: this.isAnySpecialistWorkflowActive()
        ? (this.resolvedAnySpecialistSelection?.specialistUserId ?? null)
        : this.usesAvailabilityCalendar
          ? (this.selectedClientPreferenceSpecialistUserId() || null)
          : (value.specialistUserId?.trim() ? value.specialistUserId.trim() : null),
      confirmationRequired: !!value.confirmationRequired
    });
  }

  public requestQuickCreateClient(): void
  {
    this.quickCreateClientRequested.emit();
  }

  protected onCalendarDateSelected(dateKey: string): void
  {
    this.pendingAnySpecialistSelection = null;
    this.pendingAnySpecialistChoiceId = '';
    this.pendingAnySpecialistTimeContext = null;
    this.resolvedAnySpecialistSelection = null;
    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.preferredFirstSlotId = null;
    this.hasUserPickedSlot = false;
    this.computedSlotIds = [];
    this.selectedDateKey = this.clampCalendarDate((dateKey?.trim() ?? '') || this.todayDateKey());
    this.applyCascadingSelectionResets();
    this.clearAppliedSuggestedSpecialistWhenScopeChanged();
    this.form.patchValue({
      firstSlotId: '',
      deviceId: '',
      anySpecialistChoiceId: '',
    }, { emitEvent: false });
    this.form.controls.firstSlotId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.deviceId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.anySpecialistChoiceId.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
    this.emitSlotScopeChanged();
  }

  protected onCalendarSlotPicked(slotId: string): void
  {
    this.hasUserPickedSlot = true;
    const current = this.form.getRawValue().firstSlotId?.trim() ?? '';
    if (current === slotId) {
      this.pendingAnySpecialistSelection = null;
      this.pendingAnySpecialistChoiceId = '';
      this.pendingAnySpecialistTimeContext = null;
      this.resolvedAnySpecialistSelection = null;
      this.form.patchValue({ firstSlotId: '', anySpecialistChoiceId: '' });
      this.computedSlotIds = [];
      return;
    }

    if (this.usesAvailabilityCalendar) {
      if (this.isAnySpecialistWorkflowActive()) {
        const context = this.resolveAnySpecialistTimeContext(slotId);
        if (!context) {
          this.pendingAnySpecialistSelection = null;
          this.pendingAnySpecialistChoiceId = '';
          this.pendingAnySpecialistTimeContext = null;
          this.validationErrorText = 'Unable to resolve selected time.';
          return;
        }

        const choices = this.resolveAnySpecialistSelectionChoices(context);
        if (choices.length === 0) {
          this.pendingAnySpecialistSelection = null;
          this.pendingAnySpecialistChoiceId = '';
          this.pendingAnySpecialistTimeContext = null;
          this.validationErrorText = 'Unable to build contiguous slot block.';
          return;
        }

        this.applyAnySpecialistSelection(choices[0]);
        return;
      }

      this.pendingAnySpecialistSelection = null;
      this.pendingAnySpecialistChoiceId = '';
      this.pendingAnySpecialistTimeContext = null;
      this.resolvedAnySpecialistSelection = null;
      this.form.patchValue({
        firstSlotId: slotId,
        deviceId: '',
        specialistUserId: this.selectedClientPreferenceSpecialistUserId(),
        anySpecialistChoiceId: '',
      });
      return;
    }

    this.form.patchValue({ firstSlotId: slotId });
  }

  protected setClientSpecialistIntent(intent: ClientSpecialistIntent): void
  {
    this.hasUserChangedClientSpecialistIntent = true;
    this.appliedSuggestedSpecialistUserId = null;
    this.appliedSuggestedSpecialistScopeKey = '';

    const currentPreference = this.form.getRawValue().clientSpecialistPreference?.trim() ?? ANY_SPECIALIST_PREFERENCE;
    const currentIntent: ClientSpecialistIntent =
      currentPreference === ANY_SPECIALIST_PREFERENCE ? 'any' : 'specific';
    if (
      currentIntent === intent &&
      !(intent === 'any' && (
        this.form.getRawValue().specialistUserId?.trim() ||
        this.form.getRawValue().firstSlotId?.trim() ||
        this.resolvedAnySpecialistSelection
      ))
    ) {
      return;
    }

    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.computedSlotIds = [];
    this.pendingAnySpecialistSelection = null;
    this.pendingAnySpecialistChoiceId = '';
    this.pendingAnySpecialistTimeContext = null;
    this.resolvedAnySpecialistSelection = null;
    this.preferredFirstSlotId = null;
    this.hasUserPickedSlot = false;

    if (intent === 'any') {
      this.form.patchValue({
        clientSpecialistPreference: ANY_SPECIALIST_PREFERENCE,
        specialistUserId: '',
        firstSlotId: '',
        deviceId: '',
        anySpecialistChoiceId: '',
      });
      return;
    }

    this.form.patchValue({
      firstSlotId: '',
      deviceId: '',
      anySpecialistChoiceId: '',
    });
  }

  protected onClientSpecialistSelectChanged(event: Event): void
  {
    this.hasUserChangedClientSpecialistIntent = true;
    this.appliedSuggestedSpecialistUserId = null;
    this.appliedSuggestedSpecialistScopeKey = '';

    const specialistUserId = (event.target as HTMLSelectElement).value?.trim() ?? '';
    if (!specialistUserId || specialistUserId === ANY_SPECIALIST_PREFERENCE) {
      this.setClientSpecialistIntent('any');
      return;
    }

    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.computedSlotIds = [];
    this.pendingAnySpecialistSelection = null;
    this.pendingAnySpecialistChoiceId = '';
    this.pendingAnySpecialistTimeContext = null;
    this.resolvedAnySpecialistSelection = null;
    this.preferredFirstSlotId = null;
    this.hasUserPickedSlot = false;
    this.form.patchValue({
      clientSpecialistPreference: specialistUserId,
      specialistUserId,
      firstSlotId: '',
      deviceId: '',
      anySpecialistChoiceId: '',
    });
  }

  protected onSpecialistSelectChanged(event: Event): void
  {
    const specialistUserId = (event.target as HTMLSelectElement).value?.trim() ?? '';
    this.hasUserChangedClientSpecialistIntent = !!specialistUserId;
    this.appliedSuggestedSpecialistUserId = null;
    this.appliedSuggestedSpecialistScopeKey = '';
    this.lastSuggestionControlScopeKey = this.currentSpecialistSuggestionScopeKey();
    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.ensureSelectedSlotStillVisible();
    this.rebuildComputedSlotIds();
    this.emitSlotScopeChanged();
  }

  protected applyAnySpecialistSelection(choice: AnySpecialistSelectionChoice): void
  {
    this.pendingAnySpecialistSelection = null;
    this.pendingAnySpecialistChoiceId = choice.specialistUserId;
    this.resolvedAnySpecialistSelection = choice;
    const firstSlot = this.availableSlots.find((slot) => slot.id === choice.firstSlotId);
    this.pendingAnySpecialistTimeContext = firstSlot
      ? { clinicId: firstSlot.clinicId, startTime: firstSlot.startTime, endTime: firstSlot.endTime }
      : null;
    this.validationErrorText = null;
    this.patchResolvedAnySpecialistSelection(choice);
    this.selectedDateKey = this.dateKey(
      this.availableSlots.find((slot) => slot.id === choice.firstSlotId)?.startTime ?? ''
    ) || this.selectedDateKey;
    this.computedSlotIds = choice.slotIds;
  }

  private restoreResolvedAnySpecialistSelection(): void
  {
    if (!this.isAnySpecialistWorkflowActive()) {
      return;
    }

    const choice = this.resolvedAnySpecialistSelection;
    if (!choice) {
      return;
    }

    this.patchResolvedAnySpecialistSelection(choice);
    this.computedSlotIds = choice.slotIds;
  }

  private restorePendingAnySpecialistChoiceFromForm(): void
  {
    if (
      !this.isAnySpecialistWorkflowActive() ||
      this.resolvedAnySpecialistSelection ||
      !this.pendingAnySpecialistSelection
    ) {
      return;
    }

    const specialistUserId = this.form.getRawValue().anySpecialistChoiceId?.trim() ?? '';
    if (!specialistUserId) {
      return;
    }

    const choice = this.pendingAnySpecialistSelection.choices.find(
      (item) => item.specialistUserId === specialistUserId,
    );
    if (!choice) {
      return;
    }

    this.applyAnySpecialistSelection(choice);
  }

  private patchResolvedAnySpecialistSelection(choice: AnySpecialistSelectionChoice): void
  {
    this.form.patchValue({
      clinicId: choice.clinicId,
      deviceId: choice.deviceId,
      firstSlotId: choice.firstSlotId,
      specialistUserId: choice.specialistUserId,
      anySpecialistChoiceId: choice.specialistUserId,
    }, { emitEvent: false });
    this.form.controls.clinicId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.deviceId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.firstSlotId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.specialistUserId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.anySpecialistChoiceId.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  protected onAnySpecialistChoiceChanged(event: Event): void
  {
    const specialistUserId = (event.target as HTMLSelectElement).value?.trim() ?? '';
    this.pendingAnySpecialistChoiceId = specialistUserId;
    if (!specialistUserId || !this.pendingAnySpecialistSelection) {
      return;
    }

    const choice = this.pendingAnySpecialistSelection.choices.find(
      (item) => item.specialistUserId === specialistUserId,
    );
    if (!choice) {
      return;
    }

    this.applyAnySpecialistSelection(choice);
  }

  protected isCalendarSlotSelectable(slot: SlotItem): boolean
  {
    if (this.isSlotInPast(slot)) {
      return false;
    }

    return slot.status === 'available' || (this.mode === 'edit' && slot.status === 'reserved');
  }

  private isCalendarSlotCapacityAvailable(slot: SlotItem): boolean
  {
    return slot.status === 'available' || (this.mode === 'edit' && slot.status === 'reserved');
  }

  private rebuildComputedSlotIds(): void
  {
    if (
      this.isAnySpecialistWorkflowActive() &&
      this.resolvedAnySpecialistSelection
    ) {
      this.computedSlotIds = this.resolvedAnySpecialistSelection.slotIds;
      return;
    }

    const value = this.form.getRawValue();
    const clinicId = value.clinicId?.trim() ?? '';

    const selectedProcedure = this.availableProcedureTypes.find(
      (item) => item.id === value.procedureTypeId
    );

    if (!selectedProcedure || !value.firstSlotId)
    {
      this.computedSlotIds = [];
      return;
    }

    const candidateSlots = this.resolveSlotBlockCandidates(
      this.usesAvailabilityCalendar
        ? this.selectedClientPreferenceSpecialistUserId()
        : (value.specialistUserId?.trim() ?? ''),
      clinicId,
    );

    const computedSlotIds = buildContiguousSlotBlock({
      firstSlotId: value.firstSlotId,
      allSlots: candidateSlots,
      requiredDurationMinutes: selectedProcedure.defaultDurationMinutes,
      slotDurationMinutes: this.slotDurationMinutes,
      allowReserved: this.mode === 'edit'
    });

    if (computedSlotIds.length === 0) {
      this.computedSlotIds = [];
      return;
    }

    const procedureRequiresDevice = !!selectedProcedure.deviceTypeId;

    if (!procedureRequiresDevice) {
      if (value.deviceId?.trim()) {
        this.form.patchValue({ deviceId: '' }, { emitEvent: false });
      }
      this.computedSlotIds = computedSlotIds;
      return;
    }

    const selectedDeviceId = this.clientIntentMode ? '' : (value.deviceId?.trim() ?? '');
    const resolvedDeviceId = this.resolveDeviceForSlotBlock(
      computedSlotIds,
      clinicId,
      selectedDeviceId,
    );

    if (!resolvedDeviceId) {
      this.computedSlotIds = [];
      return;
    }

    if (!selectedDeviceId) {
      this.form.patchValue({ deviceId: resolvedDeviceId }, { emitEvent: false });
    }

    this.computedSlotIds = computedSlotIds;
  }

  private syncDerivedFieldsFromFirstSlot(): void
  {
    const value = this.form.getRawValue();
    const firstSlotId = value.firstSlotId?.trim() ?? '';

    if (!firstSlotId) {
      return;
    }

    const firstSlot = this.selectableCalendarSlots.find((slot) => slot.id === firstSlotId);
    if (!firstSlot) {
      return;
    }

    if (!value.clinicId?.trim()) {
      this.form.patchValue({ clinicId: firstSlot.clinicId }, { emitEvent: false });
    }

    if (!value.deviceId?.trim()) {
      const autoMatchedDeviceId = this.findFirstMatchingDeviceId(firstSlot);
      if (autoMatchedDeviceId) {
        this.form.patchValue({ deviceId: autoMatchedDeviceId }, { emitEvent: false });
      }
    }

    if (!value.specialistUserId?.trim() && firstSlot.specialistUserId) {
      this.form.patchValue(
        { specialistUserId: firstSlot.specialistUserId },
        { emitEvent: false }
      );
    }

    this.selectedDateKey = this.dateKey(firstSlot.startTime);
  }

  private resolveSlotBlockCandidates(specialistUserId: string, clinicId: string): SlotItem[] {
    if (this.isAnySpecialistWorkflowActive() && specialistUserId) {
      return this.selectableSpecialistSlots(specialistUserId, clinicId);
    }

    return this.selectableCalendarSlots;
  }

  private selectableSpecialistSlots(specialistUserId: string, clinicId: string): SlotItem[] {
    return this.availableSlots
      .filter((slot) =>
        slot.specialistUserId === specialistUserId &&
        !slot.deviceId &&
        (!clinicId || slot.clinicId === clinicId) &&
        this.isCalendarSlotSelectable(slot),
      )
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  private buildContiguousSlotBlockFromTime(
    startTime: string,
    candidates: SlotItem[],
    requiredDurationMinutes: number,
  ): SlotItem[] {
    const byStartTime = new Map<string, SlotItem>();
    for (const slot of candidates) {
      if (!byStartTime.has(slot.startTime)) {
        byStartTime.set(slot.startTime, slot);
      }
    }

    const block: SlotItem[] = [];
    let cursor = startTime;
    let collectedMinutes = 0;

    while (collectedMinutes < requiredDurationMinutes) {
      const nextSlot = byStartTime.get(cursor);
      if (!nextSlot) {
        return [];
      }

      const duration = this.slotDurationMinutesBetween(nextSlot.startTime, nextSlot.endTime);
      if (duration <= 0) {
        return [];
      }

      block.push(nextSlot);
      collectedMinutes += duration;
      cursor = nextSlot.endTime;
    }

    return block;
  }

  private slotDurationMinutesBetween(startTime: string, endTime: string): number {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
      return Math.round((end - start) / 60000);
    }

    const startParts = this.extractDateTimeParts(startTime);
    const endParts = this.extractDateTimeParts(endTime);
    if (!startParts || !endParts) {
      return 0;
    }

    const startDate = new Date(
      Number(startParts.year),
      Number(startParts.month) - 1,
      Number(startParts.day),
      Number(startParts.hours),
      Number(startParts.minutes),
    ).getTime();
    const endDate = new Date(
      Number(endParts.year),
      Number(endParts.month) - 1,
      Number(endParts.day),
      Number(endParts.hours),
      Number(endParts.minutes),
    ).getTime();

    return endDate > startDate ? Math.round((endDate - startDate) / 60000) : 0;
  }

  private syncPrefillFieldsFromPreferredSlot(): void
  {
    const preferredSlotId = this.preferredFirstSlotId?.trim() ?? '';
    if (!preferredSlotId) {
      return;
    }

    const preferredSlot = this.availableSlots.find((slot) => slot.id === preferredSlotId);
    if (!preferredSlot) {
      return;
    }

    const value = this.form.getRawValue();
    const patch: Partial<{
      clinicId: string;
      deviceId: string;
      specialistUserId: string;
    }> = {};

    if (!value.clinicId?.trim() && preferredSlot.clinicId) {
      patch.clinicId = preferredSlot.clinicId;
    }

    if (!value.deviceId?.trim() && preferredSlot.deviceId) {
      patch.deviceId = preferredSlot.deviceId;
    }

    if (!value.specialistUserId?.trim() && preferredSlot.specialistUserId) {
      patch.specialistUserId = preferredSlot.specialistUserId;
    }

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch, { emitEvent: false });
    }
  }

  private syncDeviceSelectionFromProcedureType(): void
  {
    const selectedProcedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    if (!selectedProcedureTypeId) {
      // Keep current device prefill (for example, from slots page) until procedure is selected.
      return;
    }

    const selectedDeviceId = this.form.getRawValue().deviceId?.trim() ?? '';
    const compatibleDevices = this.compatibleDeviceOptions;

    if (compatibleDevices.length === 0) {
      if (selectedDeviceId) {
        this.form.patchValue({ deviceId: '' }, { emitEvent: false });
      }
      return;
    }

    const hasCurrent = compatibleDevices.some((item) => item.id === selectedDeviceId);
    if (hasCurrent) {
      return;
    }

    if (compatibleDevices.length === 1) {
      this.form.patchValue({ deviceId: compatibleDevices[0].id }, { emitEvent: false });
      return;
    }

    this.form.patchValue({ deviceId: '' }, { emitEvent: false });
  }

  private syncSpecialistSelectionFromProcedureType(): void
  {
    const selectedSpecialistUserId = this.usesAvailabilityCalendar
      ? this.selectedClientPreferenceSpecialistUserId()
      : (this.form.getRawValue().specialistUserId?.trim() ?? '');
    const selectedProcedureTypeId = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    const availableSpecialistOptions = this.specialistOptions;

    if (!selectedSpecialistUserId) {
      return;
    }

    if (
      this.isAnySpecialistWorkflowActive() &&
      selectedProcedureTypeId &&
      this.specialistCanPerformProcedure(selectedSpecialistUserId, selectedProcedureTypeId)
    ) {
      return;
    }

    const stillCompatible = availableSpecialistOptions.some((item) => item.id === selectedSpecialistUserId);
    if (stillCompatible) {
      return;
    }

    this.form.patchValue({
      specialistUserId: '',
      clientSpecialistPreference: this.usesAvailabilityCalendar
        ? ANY_SPECIALIST_PREFERENCE
        : this.form.getRawValue().clientSpecialistPreference,
    }, { emitEvent: false });
  }

  private syncInitialClientSpecialistIntentFromPrefill(): void
  {
    if (
      !this.usesAvailabilityCalendar ||
      this.mode !== 'edit' ||
      this.hasUserChangedClientSpecialistIntent
    ) {
      return;
    }

    const specialistUserId =
      this.prefill?.specialistUserId?.trim() ||
      this.form.getRawValue().specialistUserId?.trim() ||
      '';

    const nextPreference = specialistUserId || ANY_SPECIALIST_PREFERENCE;
    if ((this.form.getRawValue().clientSpecialistPreference?.trim() ?? '') === nextPreference) {
      return;
    }

    this.form.patchValue(
      {
        clientSpecialistPreference: nextPreference,
        specialistUserId,
      },
      { emitEvent: false },
    );
    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.emitSlotScopeChanged();
  }

  private applyInitialClientSpecialistIntent(): void
  {
    if (
      !this.usesAvailabilityCalendar ||
      this.mode !== 'edit' ||
      this.hasUserChangedClientSpecialistIntent
    ) {
      return;
    }

    const intent = this.initialClientSpecialistIntent;
    if (!intent) {
      return;
    }

    const specialistUserId =
      this.prefill?.specialistUserId?.trim() ||
      this.form.getRawValue().specialistUserId?.trim() ||
      '';
    const nextPreference = intent === 'specific' && specialistUserId
      ? specialistUserId
      : ANY_SPECIALIST_PREFERENCE;
    if ((this.form.getRawValue().clientSpecialistPreference?.trim() ?? '') === nextPreference) {
      return;
    }

    if (intent === 'specific') {
      this.resetAnySpecialistWorkflowState();
    }
    this.form.patchValue(
      {
        clientSpecialistPreference: nextPreference,
        specialistUserId: nextPreference === ANY_SPECIALIST_PREFERENCE ? '' : nextPreference,
      },
      { emitEvent: false },
    );
    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.emitSlotScopeChanged();
  }

  private applyCascadingSelectionResets(): void
  {
    const current = this.currentCascadeSelectionState();
    const previous = this.lastCascadeSelectionState;

    if (!previous) {
      this.lastCascadeSelectionState = current;
      return;
    }

    const clinicChanged = current.clinicId !== previous.clinicId;
    const clientChanged = current.clientId !== previous.clientId;
    const procedureChanged = current.procedureTypeId !== previous.procedureTypeId;
    const dateChanged = current.dateKey !== previous.dateKey;

    if (!clinicChanged && !clientChanged && !procedureChanged && !dateChanged) {
      return;
    }

    const hasActivePrefilledSlot = this.hasActiveSlotPrefill();
    const hasPendingPrefilledTimeWindow = this.hasPendingPrefilledTimeWindow();
    const hasActivePrefilledSpecialist = this.hasActiveSpecialistPrefill();
    const patch: Partial<{
      procedureTypeId: string;
      specialistUserId: string;
      clientSpecialistPreference: string;
      deviceId: string;
      firstSlotId: string;
      anySpecialistChoiceId: string;
    }> = {
      anySpecialistChoiceId: '',
    };

    if (clinicChanged) {
      patch.procedureTypeId = '';
      patch.specialistUserId = '';
      patch.clientSpecialistPreference = ANY_SPECIALIST_PREFERENCE;
      patch.deviceId = '';
      patch.firstSlotId = '';
    } else if (procedureChanged) {
      if (!hasActivePrefilledSlot && !hasPendingPrefilledTimeWindow) {
        patch.specialistUserId = '';
        patch.clientSpecialistPreference = ANY_SPECIALIST_PREFERENCE;
        patch.deviceId = '';
        patch.firstSlotId = '';
      }
    } else if (dateChanged) {
      patch.specialistUserId = '';
      patch.clientSpecialistPreference = ANY_SPECIALIST_PREFERENCE;
      patch.firstSlotId = '';
    } else if (clientChanged) {
      if (!hasActivePrefilledSlot && !hasPendingPrefilledTimeWindow) {
        patch.specialistUserId = '';
        patch.clientSpecialistPreference = ANY_SPECIALIST_PREFERENCE;
        patch.firstSlotId = '';
      }
    }

    this.hasUserChangedClientSpecialistIntent = hasActivePrefilledSpecialist;
    this.hasUserPickedSlot = false;
    if (!hasActivePrefilledSlot) {
      this.preferredFirstSlotId = null;
    }
    this.appliedSuggestedSpecialistUserId = null;
    this.appliedSuggestedSpecialistScopeKey = '';
    this.lastSuggestionControlScopeKey = this.currentSpecialistSuggestionScopeKey();
    this.resetAnySpecialistWorkflowState();
    this.form.patchValue(patch, { emitEvent: false });
    this.computedSlotIds = [];

    const nextValue = this.form.getRawValue();
    this.lastCascadeSelectionState = {
      clinicId: nextValue.clinicId?.trim() ?? '',
      clientId: nextValue.clientId?.trim() ?? '',
      procedureTypeId: nextValue.procedureTypeId?.trim() ?? '',
      dateKey: this.selectedDateKey?.trim() ?? '',
    };
  }

  private currentCascadeSelectionState(): AppointmentFormCascadeSelectionState
  {
    const value = this.form.getRawValue();
    return {
      clinicId: value.clinicId?.trim() ?? '',
      clientId: value.clientId?.trim() ?? '',
      procedureTypeId: value.procedureTypeId?.trim() ?? '',
      dateKey: this.selectedDateKey?.trim() ?? '',
    };
  }

  private applySuggestedSpecialist(): void
  {
    if (
      this.mode !== 'create' ||
      this.hasUserChangedClientSpecialistIntent ||
      this.hasActiveSpecialistPrefill()
    ) {
      return;
    }

    const currentPreference = this.form.getRawValue().clientSpecialistPreference?.trim()
      || ANY_SPECIALIST_PREFERENCE;
    const currentSpecialistUserId = this.form.getRawValue().specialistUserId?.trim() ?? '';
    const currentFirstSlotId = this.form.getRawValue().firstSlotId?.trim() ?? '';
    const currentDeviceId = this.form.getRawValue().deviceId?.trim() ?? '';
    const hasActiveSlotPrefill = this.hasActiveSlotPrefill();
    const currentSelection = this.usesAvailabilityCalendar
      ? currentPreference
      : currentSpecialistUserId;
    const suggestion = this.suggestedSpecialistUserId?.trim() ?? '';

    if (!suggestion) {
      if (
        this.appliedSuggestedSpecialistUserId &&
        currentSelection === this.appliedSuggestedSpecialistUserId
      ) {
        this.appliedSuggestedSpecialistUserId = null;
        this.appliedSuggestedSpecialistScopeKey = '';
        this.resetAnySpecialistWorkflowState();
        this.form.patchValue(
          {
            clientSpecialistPreference: ANY_SPECIALIST_PREFERENCE,
            specialistUserId: '',
            firstSlotId: hasActiveSlotPrefill ? currentFirstSlotId : '',
            deviceId: hasActiveSlotPrefill ? currentDeviceId : '',
            anySpecialistChoiceId: '',
          },
          { emitEvent: false },
        );
        this.invalidateFilteredCalendarSlotsCache();
        this.invalidateCalendarHighlightedSlotIdsCache();
        this.rebuildComputedSlotIds();
        this.emitSlotScopeChanged();
      }
      return;
    }

    if (
      currentSelection &&
      currentSelection !== ANY_SPECIALIST_PREFERENCE &&
      currentSelection !== this.appliedSuggestedSpecialistUserId
    ) {
      return;
    }

    if (!this.specialistOptions.some((item) => item.id === suggestion)) {
      return;
    }

    if (currentSelection === suggestion) {
      this.appliedSuggestedSpecialistUserId = suggestion;
      this.appliedSuggestedSpecialistScopeKey = this.currentSpecialistSuggestionScopeKey();
      this.lastSuggestionControlScopeKey = this.appliedSuggestedSpecialistScopeKey;
      return;
    }

    this.appliedSuggestedSpecialistUserId = suggestion;
    this.appliedSuggestedSpecialistScopeKey = this.currentSpecialistSuggestionScopeKey();
    this.lastSuggestionControlScopeKey = this.appliedSuggestedSpecialistScopeKey;
    this.resetAnySpecialistWorkflowState();
    this.form.patchValue(
      {
        clientSpecialistPreference: this.usesAvailabilityCalendar
          ? suggestion
          : this.form.getRawValue().clientSpecialistPreference,
        specialistUserId: suggestion,
        firstSlotId: hasActiveSlotPrefill ? currentFirstSlotId : '',
        deviceId: hasActiveSlotPrefill ? currentDeviceId : '',
        anySpecialistChoiceId: '',
      },
      { emitEvent: false },
    );
    this.invalidateFilteredCalendarSlotsCache();
    this.invalidateCalendarHighlightedSlotIdsCache();
    this.ensureSelectedSlotStillVisible();
    this.rebuildComputedSlotIds();
    this.emitSlotScopeChanged();
  }

  private clearAppliedSuggestedSpecialistWhenScopeChanged(): void
  {
    const currentScopeKey = this.currentSpecialistSuggestionScopeKey();
    if (!this.lastSuggestionControlScopeKey) {
      this.lastSuggestionControlScopeKey = currentScopeKey;
      return;
    }

    if (currentScopeKey === this.lastSuggestionControlScopeKey) {
      return;
    }

    this.lastSuggestionControlScopeKey = currentScopeKey;
    this.appliedSuggestedSpecialistUserId = null;
    this.appliedSuggestedSpecialistScopeKey = '';

    if (
      this.mode !== 'create' ||
      this.hasUserChangedClientSpecialistIntent ||
      this.hasActiveSpecialistPrefill()
    ) {
      return;
    }

    const currentPreference = this.form.getRawValue().clientSpecialistPreference?.trim()
      || ANY_SPECIALIST_PREFERENCE;
    const currentSpecialistUserId = this.form.getRawValue().specialistUserId?.trim() ?? '';
    const currentFirstSlotId = this.form.getRawValue().firstSlotId?.trim() ?? '';
    const currentDeviceId = this.form.getRawValue().deviceId?.trim() ?? '';
    const hasActiveSlotPrefill = this.hasActiveSlotPrefill();
    const currentSelection = this.usesAvailabilityCalendar
      ? currentPreference
      : currentSpecialistUserId;
    if (!currentSelection || currentSelection === ANY_SPECIALIST_PREFERENCE) {
      return;
    }

    this.resetAnySpecialistWorkflowState();
    this.form.patchValue(
      {
        clientSpecialistPreference: ANY_SPECIALIST_PREFERENCE,
        specialistUserId: '',
        firstSlotId: hasActiveSlotPrefill ? currentFirstSlotId : '',
        deviceId: hasActiveSlotPrefill ? currentDeviceId : '',
        anySpecialistChoiceId: '',
      },
      { emitEvent: false },
    );
  }

  private hasActiveSpecialistPrefill(): boolean
  {
    const prefillSpecialistUserId = this.prefill?.specialistUserId?.trim() ?? '';
    if (!prefillSpecialistUserId) {
      return false;
    }

    return this.hasActiveSlotPrefill();
  }

  private hasActiveSlotPrefill(): boolean
  {
    const prefillFirstSlotId = this.prefill?.firstSlotId?.trim() ?? '';
    const currentFirstSlotId = this.form.getRawValue().firstSlotId?.trim() ?? '';
    return !!prefillFirstSlotId && currentFirstSlotId === prefillFirstSlotId;
  }

  private hasPendingPrefilledTimeWindow(): boolean
  {
    return !!this.prefill?.preserveTimeOnly &&
      !!this.prefill.firstSlotStartTime?.trim() &&
      !!this.prefill.firstSlotEndTime?.trim() &&
      !this.form.getRawValue().firstSlotId?.trim();
  }

  private currentSpecialistSuggestionScopeKey(): string
  {
    const value = this.form.getRawValue();
    return [
      value.clientId?.trim() ?? '',
      value.procedureTypeId?.trim() ?? '',
      value.clinicId?.trim() ?? '',
      this.selectedDateKey?.trim() ?? '',
    ].join('|');
  }

  private isAnySpecialistWorkflowActive(): boolean
  {
    return (
      this.usesAvailabilityCalendar &&
      (this.form.getRawValue().clientSpecialistPreference?.trim() || ANY_SPECIALIST_PREFERENCE) === ANY_SPECIALIST_PREFERENCE
    );
  }

  private shouldSelectPrefilledSlotOnOpen(prefillFirstSlotId: string): boolean
  {
    return this.selectPrefilledSlotOnOpen &&
      this.mode === 'create' &&
      !this.usesAvailabilityCalendar &&
      prefillFirstSlotId.length > 0;
  }

  private selectedClientPreferenceSpecialistUserId(): string
  {
    if (!this.usesAvailabilityCalendar) {
      return this.form.getRawValue().specialistUserId?.trim() ?? '';
    }

    const preference = this.form.getRawValue().clientSpecialistPreference?.trim() ?? ANY_SPECIALIST_PREFERENCE;
    return preference === ANY_SPECIALIST_PREFERENCE ? '' : preference;
  }

  private resetAnySpecialistWorkflowState(): void
  {
    this.pendingAnySpecialistSelection = null;
    this.pendingAnySpecialistChoiceId = '';
    this.pendingAnySpecialistTimeContext = null;
    this.resolvedAnySpecialistSelection = null;
    this.form.patchValue({ anySpecialistChoiceId: '' }, { emitEvent: false });
    this.form.controls.anySpecialistChoiceId.updateValueAndValidity({ emitEvent: false });
  }

  private ensureSelectedSlotStillVisible(): void
  {
    let firstSlotId = this.form.getRawValue().firstSlotId?.trim() ?? '';

    // Keep auto-prefill behavior from slots page until user explicitly picks another slot.
    if (!this.hasUserPickedSlot && this.preferredFirstSlotId) {
      const preferredMatch = this.filteredCalendarSlots.find(
        (slot) => slot.id === this.preferredFirstSlotId && this.isCalendarSlotSelectable(slot),
      );
      if (preferredMatch) {
        firstSlotId = this.preferredFirstSlotId;
        this.form.patchValue({ firstSlotId }, { emitEvent: false });
      }
    }

    if (!firstSlotId) {
      const replacementSlot = this.findVisibleReplacementForSelectedSlot('');
      if (replacementSlot) {
        this.form.patchValue({ firstSlotId: replacementSlot.id }, { emitEvent: false });
      }

      return;
    }

    // Slots can be loaded asynchronously after prefill; do not clear selection before data is available.
    if (this.filteredCalendarSlots.length === 0) {
      return;
    }

    const matchedSlot = this.findVisibleSelectedSlot(firstSlotId);
    if (!matchedSlot) {
      const replacementSlot = this.findVisibleReplacementForSelectedSlot(firstSlotId);
      if (replacementSlot) {
        this.form.patchValue({ firstSlotId: replacementSlot.id }, { emitEvent: false });
        return;
      }

      this.form.patchValue({ firstSlotId: '' }, { emitEvent: false });
      return;
    }

    const slotDateKey = this.dateKey(matchedSlot.startTime);
    if (slotDateKey && slotDateKey !== this.selectedDateKey) {
      this.selectedDateKey = slotDateKey;
    }
  }

  private findVisibleSelectedSlot(firstSlotId: string): SlotItem | AppointmentCalendarSlot | null
  {
    const matchedCalendarSlot = this.filteredCalendarSlots.find(
      (slot) => slot.id === firstSlotId && this.isCalendarSlotSelectable(slot),
    );
    if (matchedCalendarSlot) {
      return matchedCalendarSlot;
    }

    if (!this.isAnySpecialistWorkflowActive()) {
      return null;
    }

    const value = this.form.getRawValue();
    const specialistUserId = value.specialistUserId?.trim() ?? '';
    const clinicId = value.clinicId?.trim() ?? '';
    if (!specialistUserId) {
      return null;
    }

    return this.selectableSpecialistSlots(specialistUserId, clinicId)
      .find((slot) => slot.id === firstSlotId)
      ?? null;
  }

  private findVisibleReplacementForSelectedSlot(firstSlotId: string): SlotItem | AppointmentCalendarSlot | null
  {
    const sourceSlot = this.availableSlots.find((slot) => slot.id === firstSlotId);
    const clinicId = sourceSlot?.clinicId ?? this.prefill?.clinicId?.trim() ?? '';
    const startTime = sourceSlot?.startTime ?? this.prefill?.firstSlotStartTime?.trim() ?? '';
    const endTime = sourceSlot?.endTime ?? this.prefill?.firstSlotEndTime?.trim() ?? '';

    if (!clinicId || !startTime || !endTime) {
      return null;
    }

    return this.filteredCalendarSlots.find((slot) =>
      (!firstSlotId || slot.id !== firstSlotId) &&
      slot.clinicId === clinicId &&
      slot.startTime === startTime &&
      slot.endTime === endTime &&
      this.isCalendarSlotSelectable(slot),
    ) ?? null;
  }

  private findCalendarSlotIdForActualSlotId(slotId: string): string | null
  {
    if (!this.isAnySpecialistWorkflowActive()) {
      return null;
    }

    const actualSlot = this.availableSlots.find((slot) => slot.id === slotId);
    if (!actualSlot) {
      return null;
    }

    return this.anySpecialistCalendarSlotId(
      actualSlot.clinicId,
      actualSlot.startTime,
      actualSlot.endTime,
    );
  }

  private anySpecialistChoiceHighlightedSlotIds(choices: AnySpecialistSelectionChoice[]): string[]
  {
    const highlighted = new Set<string>();
    for (const choice of choices) {
      for (const slotId of choice.slotIds) {
        highlighted.add(this.findCalendarSlotIdForActualSlotId(slotId) ?? slotId);
      }
    }

    return Array.from(highlighted);
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }

  protected showIdMeta(value: string | null | undefined): boolean
  {
    if (this.hideTechnicalIds) {
      return false;
    }

    const normalized = value?.trim() ?? '';
    return this.isUuidLike(normalized) || normalized.length > 26;
  }

  protected slotDisplayLabel(slotId: string): string
  {
    const slot = this.availableSlots.find((item) => item.id === slotId);

    if (!slot) {
      return this.readableId(slotId);
    }

    return this.formatSlotRange(slot.startTime, slot.endTime);
  }

  protected slotDisplayTitle(slotId: string): string
  {
    const slot = this.availableSlots.find((item) => item.id === slotId);

    if (!slot) {
      return slotId;
    }

    return `${this.formatSlotRange(slot.startTime, slot.endTime)} (${slotId})`;
  }

  private specialistLabel(specialistUserId: string | null | undefined): string
  {
    const normalized = specialistUserId?.trim() ?? '';
    if (!normalized) {
      return 'Specialist';
    }

    return this.specialists.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
  }

  private deviceLabel(deviceId: string | null | undefined): string
  {
    const normalized = deviceId?.trim() ?? '';
    if (!normalized) {
      return '';
    }

    return this.devices.find((item) => item.id === normalized)?.label ?? this.readableId(normalized);
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  protected get currentSchedulingTimeZone(): string | null
  {
    const clinicId = this.form?.getRawValue?.().clinicId?.trim() ?? '';
    if (clinicId) {
      return this.clinics.find((item) => item.id === clinicId)?.timeZone?.trim() || null;
    }

    if (this.clinics.length === 1) {
      return this.clinics[0].timeZone?.trim() || null;
    }

    if (this.clientIntentMode) {
      const slotTimeZones = Array.from(new Set(
        this.filteredCalendarSlots
          .map((slot) => slot.clinicTimeZone?.trim() ?? '')
          .filter((timeZone) => timeZone.length > 0),
      ));
      if (slotTimeZones.length === 1) {
        return slotTimeZones[0];
      }
    }

    return null;
  }

  private formatSlotRange(startTime: string, endTime: string): string
  {
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return `${startTime} -> ${endTime}`;
    }

    const timeZone = this.currentSchedulingTimeZone ?? undefined;
    const dateText = new Intl.DateTimeFormat(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(start);
    const timeText = new Intl.DateTimeFormat(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    });

    return `${dateText} ${timeText.format(start)} -> ${timeText.format(end)}`;
  }

  private buildValidationErrorText(): string
  {
    const messages: string[] = [];

    if (this.form.controls.clinicId.invalid && !this.clientIntentMode) {
      messages.push('Clinic is required.');
    }

    if (this.form.controls.clientId.invalid) {
      messages.push('Client is required.');
    }

    if (this.form.controls.procedureTypeId.invalid) {
      messages.push('Procedure type is required.');
    }

    if (this.form.controls.specialistUserId.invalid && !this.isAnySpecialistWorkflowActive()) {
      messages.push('Specialist is required.');
    }

    if (
      this.isAnySpecialistWorkflowActive() &&
      this.pendingAnySpecialistSelection &&
      !this.resolvedAnySpecialistSelection
    ) {
      messages.push('Choose specialist.');
    } else if (this.form.controls.firstSlotId.invalid) {
      messages.push(this.usesAvailabilityCalendar ? 'Choose an available time.' : 'Start slot is required.');
    }

    if (messages.length === 0) {
      return 'Please fill all required fields.';
    }

    return messages.join(' ');
  }

  private dateKey(value: string): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '';
    }

    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: this.currentSchedulingTimeZone ?? undefined,
      }).format(date);
    }

    const parts = this.extractDateTimeParts(normalized);
    if (parts) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }

    return normalized.slice(0, 10);
  }

  private extractDateTimeParts(
    value: string,
  ): { year: string; month: string; day: string; hours: string; minutes: string } | null
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      return null;
    }

    const [, year, month, day, hours, minutes] = match;
    return { year, month, day, hours, minutes };
  }

  protected isSlotInPast(slot: SlotItem): boolean
  {
    const slotStart = this.parseSlotDateToMs(slot.startTime);
    if (Number.isNaN(slotStart)) {
      return false;
    }

    return slotStart < Date.now();
  }

  private todayDateKey(): string
  {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: this.currentSchedulingTimeZone ?? undefined,
    }).format(new Date());
  }

  private ensureSelectedDateWithinMin(): void
  {
    const clampedDate = this.clampCalendarDate(this.selectedDateKey);
    if (clampedDate === this.selectedDateKey) {
      return;
    }

    this.selectedDateKey = clampedDate;
    this.form.patchValue({ firstSlotId: '', anySpecialistChoiceId: '' }, { emitEvent: false });
    this.computedSlotIds = [];
    this.emitSlotScopeChanged();
  }

  private clampCalendarDate(dateKey: string): string
  {
    const normalized = dateKey?.trim() ?? '';
    const fallback = normalized || this.todayDateKey();
    const minDate = this.minCalendarDate?.trim() ?? '';
    return minDate && fallback < minDate ? minDate : fallback;
  }

  private parseSlotDateToMs(value: string): number
  {
    const parsed = new Date(value).getTime();
    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    const parts = this.extractDateTimeParts(value);
    if (!parts) {
      return Number.NaN;
    }

    const year = Number(parts.year);
    const month = Number(parts.month);
    const day = Number(parts.day);
    const hours = Number(parts.hours);
    const minutes = Number(parts.minutes);
    return new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
  }

  private emitSlotScopeChanged(): void
  {
    const value = this.form.getRawValue();
    const clientId = value.clientId?.trim() ?? '';
    const clinicId = value.clinicId?.trim() ?? '';
    const schedulingDate = this.selectedDateKey?.trim() ?? '';
    const procedureTypeId = value.procedureTypeId?.trim() ?? '';
    const specialistUserId = this.isAnySpecialistWorkflowActive()
      ? ''
      : this.selectedClientPreferenceSpecialistUserId();
    const deviceId = value.deviceId?.trim() ?? '';

    const nextKey = [clientId, clinicId, schedulingDate, procedureTypeId, specialistUserId, deviceId].join('|');
    if (nextKey === this.lastEmittedSlotScopeKey) {
      return;
    }

    this.lastEmittedSlotScopeKey = nextKey;
    this.slotScopeChanged.emit({
      clientId,
      clinicId: clinicId || undefined,
      schedulingDate,
      procedureTypeId,
      specialistUserId: specialistUserId || undefined,
      deviceId: deviceId || undefined,
    });
  }

  private hasMatchingDeviceSlot(
    deviceId: string,
    startTime: string,
    endTime: string,
    clinicId: string,
  ): boolean {
    return this.selectableDeviceSlotKeys.has(this.deviceSlotKey(deviceId, startTime, endTime, clinicId));
  }

  private hasAnyCompatibleDeviceSlot(
    compatibleDeviceIds: Set<string>,
    startTime: string,
    endTime: string,
    clinicId: string,
  ): boolean {
    for (const deviceId of compatibleDeviceIds) {
      if (this.hasMatchingDeviceSlot(deviceId, startTime, endTime, clinicId)) {
        return true;
      }
    }

    return false;
  }

  private countAvailableCompatibleDevices(
    compatibleDeviceIds: Set<string>,
    startTime: string,
    endTime: string,
    clinicId: string,
  ): number {
    const availableDeviceIds = this.availableDeviceIdsByTimeKey.get(
      this.deviceTimeKey(startTime, endTime, clinicId),
    );
    if (!availableDeviceIds) {
      return 0;
    }

    let count = 0;
    for (const deviceId of compatibleDeviceIds) {
      if (availableDeviceIds.has(deviceId)) {
        count += 1;
      }
    }

    return count;
  }

  private resolveDeviceForTimeBlock(
    blockSlots: SlotItem[],
    clinicId: string,
    compatibleDeviceIds: Set<string>,
  ): string | null {
    if (blockSlots.length === 0) {
      return null;
    }

    for (const deviceId of compatibleDeviceIds) {
      const matchesAll = blockSlots.every((slot) =>
        this.hasMatchingDeviceSlot(deviceId, slot.startTime, slot.endTime, clinicId),
      );
      if (matchesAll) {
        return deviceId;
      }
    }

    return null;
  }

  private specialistCanPerformProcedure(specialistUserId: string, procedureTypeId: string): boolean {
    const procedureTypeIds = this.specialistProcedureIdsById.get(specialistUserId);
    return !procedureTypeIds || procedureTypeIds.size === 0 || procedureTypeIds.has(procedureTypeId);
  }

  private deviceSlotKey(deviceId: string, startTime: string, endTime: string, clinicId: string): string {
    return `${clinicId || '*'}|${deviceId}|${startTime}|${endTime}`;
  }

  private deviceTimeKey(startTime: string, endTime: string, clinicId: string): string {
    return `${clinicId || '*'}|${startTime}|${endTime}`;
  }

  protected get availableProcedureTypes(): ProcedureTypeItem[]
  {
    const value = this.form.getRawValue();
    const clinicId = value.clinicId?.trim() ?? '';

    const activeProcedureTypes = this.procedureTypes.filter((item) => item.isActive);

    const clinicAssignedIds = new Set(this.clinicProcedureAssignments[clinicId] ?? []);
    let filtered = clinicId
      ? activeProcedureTypes.filter((item) => clinicAssignedIds.has(item.id))
      : activeProcedureTypes;

    if (!clinicId) {
      return filtered;
    }

    const deviceTypesByClinic = new Set(
      this.deviceOptions
        .filter((device) => !device.clinicId || device.clinicId === clinicId)
        .map((device) => device.deviceTypeId?.trim() ?? '')
        .filter((deviceTypeId) => deviceTypeId.length > 0),
    );

    return filtered.filter((procedure) =>
      !procedure.deviceTypeId || deviceTypesByClinic.has(procedure.deviceTypeId),
    );
  }

  private ensureProcedureSelectionAllowed(): void
  {
    const selected = this.form.getRawValue().procedureTypeId?.trim() ?? '';
    if (!selected) {
      return;
    }

    const allowedIds = new Set(this.availableProcedureTypes.map((item) => item.id));
    if (allowedIds.has(selected)) {
      return;
    }

    this.form.patchValue(
      {
        procedureTypeId: '',
        deviceId: '',
        firstSlotId: ''
      },
      { emitEvent: false }
    );
    this.computedSlotIds = [];
  }

  private findFirstMatchingDeviceId(slot: SlotItem): string | null {
    const clinicId = this.form.getRawValue().clinicId?.trim() ?? '';
    const compatibleDeviceIds = new Set(
      this.compatibleDeviceOptions.map((item) => item.id),
    );

    for (const option of this.compatibleDeviceOptions) {
      if (!compatibleDeviceIds.has(option.id)) {
        continue;
      }

      if (this.hasMatchingDeviceSlot(option.id, slot.startTime, slot.endTime, clinicId)) {
        return option.id;
      }
    }

    return null;
  }

  private resolveDeviceForSlotBlock(
    slotIds: string[],
    clinicId: string,
    selectedDeviceId: string
  ): string | null {
    if (slotIds.length === 0) {
      return null;
    }

    const slotsById = new Map(this.availableSlots.map((slot) => [slot.id, slot]));
    const blockSlots = slotIds
      .map((slotId) => slotsById.get(slotId))
      .filter((slot): slot is SlotItem => !!slot);

    if (blockSlots.length !== slotIds.length) {
      return null;
    }

    const candidates = selectedDeviceId
      ? this.compatibleDeviceOptions.filter((option) => option.id === selectedDeviceId)
      : this.compatibleDeviceOptions;

    for (const option of candidates) {
      const matchesAll = blockSlots.every((slot) =>
        this.hasMatchingDeviceSlot(option.id, slot.startTime, slot.endTime, clinicId),
      );

      if (matchesAll) {
        return option.id;
      }
    }

    return null;
  }
}
